import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from 'moment';

import { useAlert } from 'src/hooks/useAlert';
import { useSubmit } from 'src/hooks/useSubmit';
import useFetch from 'src/hooks/useFetch';
import { formatFinalValue } from './utils';

import api from 'src/services/api';

import { Container } from 'src/components/Layouts/Container';
import { Centered } from 'src/components/Layouts/Container/styles';
import EnderecoForm from 'src/modules/usuario/components/EnderecoForm';

import { InputButton, useStyles } from './styles';

export function PagamentoComBoleto(props) {
  const location = useLocation();

  const classes = useStyles(props);

  const { type, plano } = location.state;

  const usuario = useSelector((state) => state.user);

  const { alert, openAlert, closeAlert } = useAlert();

  const [formSubmiting, setFormSubmiting] = useState(false);
  const [cupom, setCupom] = useState('');

  const [isAddressValid, setIsAddressValid] = useState(false);
  const [inputedAdress, setInputtedAddress] = useState(null);
  const [bankSlipResponse, setBankSlipResponse] = useState(null);

  const [loadedAddress, isLoadingAdress] = useFetch('/usuario/endereco');

  const [submitCupom, cupomValidado, isCupomSubmiting] = useSubmit(async () => {
    return api.post('/validarCupom', {
      tipo_utilizacao: type,
      cupom,
      id_usuario: usuario.id,
    });
  });

  const handleServicoPagamento = async () => {
    try {
      setFormSubmiting(true);

      const item = {
        description: `XMEI - ${plano.nome}`,
        quantity: 1,
        price_cents: parseFloat(plano.valor).toFixed(2) * 100,
      };

      const request_body = {
        method: 'bank_slip',
        email: usuario.email,
        cpf_cnpj: usuario.cpf,
        items: [item],
        zip_code: loadedAddress?.cep ?? inputedAdress?.cep,
        number: loadedAddress?.numero ?? inputedAdress?.numero,
      };

      if (cupomValidado) {
        request_body.cupom = cupomValidado;
      }

      const response = await api.post('/realizarCobrancaDireta', request_body);

      const fatura = await api.get(
        `/buscarFatura/${response.data.boleto.invoice_id}`
      );

      const bankSlip = { ...response.data.boleto };

      const dataVencimento = fatura.data.fatura.due_date.split('-');

      bankSlip.due_date = `${dataVencimento[2]}/${dataVencimento[1]}/${dataVencimento[0]}`;

      setBankSlipResponse(bankSlip);

      setFormSubmiting(false);
    } catch (error) {
      setFormSubmiting(false);

      openAlert('Falha ao tentar gerar boleto!', 'error');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!loadedAddress && inputedAdress) {
        setFormSubmiting(true);

        await api.post('/enderecoUsuario', {
          id_usuario: usuario.id,
          ...inputedAdress,
        });
      }

      handleServicoPagamento();
    } catch (error) {
      setFormSubmiting(false);

      openAlert('Falha ao tentar registrar endereço!', 'error');
    }
  };

  return (
    <Container maxwidth={800} isLoading={isLoadingAdress}>
      <Collapse in={alert.open}>
        <Alert severity={alert.severity} onClick={closeAlert}>
          {alert.message}
        </Alert>
      </Collapse>

      <>
        <div className={classes.titleBankSlip}>
          <Typography className={classes.textContent}>
            DETALHES DO BOLETO
          </Typography>
        </div>
        <div className={classes.marginBottom}>
          <Typography className={classes.label}>Descrição Serviço</Typography>
          <Typography className={classes.textContent}>{plano.nome}</Typography>
        </div>
        <div className={classes.marginBottom}>
          <Typography className={classes.label}>Pagador</Typography>
          <Typography className={classes.textContent}>
            {usuario.nome}
          </Typography>
        </div>
        <div className={classes.viewRow}>
          <div>
            <Typography className={classes.label}>Emissão</Typography>
            <Typography className={classes.textContent}>
              {moment(new Date(), 'DD/MM/YYYY').format('DD/MM/YYYY')}
            </Typography>
          </div>
          <div className={classes.viewValue}>
            <Typography className={classes.label}>Valor</Typography>
            <Typography className={classes.textContent}>
              {`R$ ${parseFloat(plano.valor).toFixed(2)}`}
            </Typography>
          </div>
          {cupomValidado?.status === 'success' && (
            <div className={classes.viewValue}>
              <Typography className={classes.label}>
                Valor final com desconto
              </Typography>
              <Typography className={classes.textContent}>
                {formatFinalValue(plano.valor, cupomValidado)}
              </Typography>
            </div>
          )}
        </div>
        {bankSlipResponse && (
          <div className={classes.viewColumn}>
            <div>
              <Typography className={classes.label}>Vencimento</Typography>
              <Typography className={classes.textContent}>
                {bankSlipResponse.due_date}
              </Typography>
            </div>
            <div>
              <Typography className={classes.label}>
                Código de barras
              </Typography>
              <Typography className={classes.textContent}>
                {bankSlipResponse.identification}
              </Typography>
            </div>
            <Box mt={2}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                target="_blank"
                href={bankSlipResponse.url}
              >
                Abrir boleto
              </Button>
            </Box>
          </div>
        )}
        {!bankSlipResponse && (
          <Grid container spacing={4} className={classes.cupomContainer}>
            <Grid item xs={6}>
              <TextField
                name="cupom"
                label="Cupom promocional"
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                error={cupomValidado?.status === 'warning'}
                helperText={cupomValidado?.message}
                onChange={(event) => setCupom(event.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <InputButton
                size="large"
                variant="contained"
                fullWidth
                disabled={isCupomSubmiting || cupom === ''}
                onClick={submitCupom}
              >
                Aplicar cupom
              </InputButton>
            </Grid>
          </Grid>
        )}

        {!loadedAddress && !bankSlipResponse && (
          <Box className={classes.titleBankSlip} mt={2}>
            <Typography className={classes.textContent}>
              COMPLETE OS SEUS DADOS
            </Typography>
            <Box mt={2}>
              <EnderecoForm
                onChange={setInputtedAddress}
                onValid={setIsAddressValid}
              />
            </Box>
          </Box>
        )}

        {!bankSlipResponse && (
          <Centered>
            <Box marginTop={4}>
              <Button
                size="large"
                variant="contained"
                color="primary"
                disabled={(!loadedAddress && !isAddressValid) || formSubmiting}
                onClick={handleSubmit}
              >
                {formSubmiting ? (
                  <CircularProgress color="primary" size={25} />
                ) : (
                  'Confirmar solicitação'
                )}
              </Button>
            </Box>
          </Centered>
        )}
      </>
    </Container>
  );
}

export default PagamentoComBoleto;
